import { useState, useCallback, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth.store';
import { SelfHostedLoginCaptcha } from '../features/auth/components/SelfHostedLoginCaptcha';
import { useLogin, useRuntimeCaptcha } from '../features/auth';

export function LoginPage() {
  const token = useAuthStore((s) => s.accessToken);
  const navigate = useNavigate();
  const { doLogin, loading, error } = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selfHostedOpen, setSelfHostedOpen] = useState(false);

  const handleCaptchaVerified = useCallback(
    async (captcha: {
      captchaDeviceKey?: string;
      captchaVerifyParam?: string;
      captchaTicket?: string;
      captchaRandstr?: string;
    }) => {
      setSelfHostedOpen(false);
      const ok = await doLogin({ email, password, ...captcha });
      if (ok) {
        navigate('/', { replace: true });
      }
    },
    [email, password, doLogin, navigate],
  );

  const {
    activeProvider,
    enabled: captchaEnabled,
    ready: captchaReady,
    loading: captchaLoading,
    error: captchaError,
    trigger: triggerCaptcha,
    reload: reloadCaptchaConfig,
    captchaElementId,
    captchaButtonId,
  } = useRuntimeCaptcha(handleCaptchaVerified);

  // Already logged in → go to home
  if (token) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (captchaEnabled && activeProvider === 'self_hosted') {
      setSelfHostedOpen(true);
    } else if (captchaEnabled) {
      triggerCaptcha();
    } else {
      const ok = await doLogin({ email, password });
      if (ok) {
        navigate('/', { replace: true });
      }
    }
  };

  // Disable button while login is in progress or captcha is still loading
  const isDisabled = loading || (captchaEnabled && !captchaReady);

  let buttonText = '登录';
  if (loading) {
    buttonText = '登录中...';
  } else if (captchaEnabled && (captchaLoading || (!captchaReady && !captchaError))) {
    buttonText = '加载中...';
  }

  const displayError = error || captchaError;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-semibold text-gray-900 text-center mb-8">
          体验官管理后台
        </h1>

        {selfHostedOpen && activeProvider === 'self_hosted' && (
          <SelfHostedLoginCaptcha email={email} onVerified={handleCaptchaVerified} onClose={() => setSelfHostedOpen(false)} />
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              邮箱
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-gray-300 text-sm
                focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              密码
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-gray-300 text-sm
                focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              placeholder="请输入密码"
            />
          </div>

          {/* Hidden captcha mount points — popup renders as SDK-managed overlay */}
          {captchaEnabled && activeProvider === 'aliyun' && (
            <>
              <div id={captchaElementId} style={{ display: 'none' }} />
              <button id={captchaButtonId} type="button" style={{ display: 'none' }} aria-hidden="true" />
            </>
          )}

          {displayError && (
            <div className="space-y-2">
              <div className="text-sm text-red-500">{displayError}</div>
              {captchaError && (
                <button
                  type="button"
                  onClick={() => void reloadCaptchaConfig()}
                  className="text-xs text-primary hover:text-primary-hover transition-colors"
                >
                  重新加载验证配置
                </button>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isDisabled}
            className="w-full h-9 rounded-md bg-primary text-white text-sm font-medium
              hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {buttonText}
          </button>
        </form>
      </div>
    </div>
  );
}
